import { ROUTES_TOUR } from './tourRoutes';
import { TOUR_ANCHOR, TourAnchor } from './tourAnchors';

// A tour step is a journey through the real product: read the explainer, unlock
// exactly one menu item, land on the real page, then spotlight the real control.
// The canonical step keys. Exported as a const object so every consumer
// (the shell's page dispatch, WALKTHROUGH_BY_STEP, readiness checks) references
// the same symbol and a rename is a compile error, not a silently dead branch.
export const TOUR_STEP = {
  welcome: 'welcome',
  plans: 'plans',
  accounts: 'accounts',
  copier: 'copier',
  logs: 'logs',
  community: 'community'
} as const;

export type TourStepKey = (typeof TOUR_STEP)[keyof typeof TOUR_STEP];

export interface TourStepDef {
  key: TourStepKey;
  railTitle: string;
  railHint: string;
  // Menu item id unlocked while pointing. Undefined = explainer only, no handoff.
  navMenuId?: string;
  route?: string;
  // The control to spotlight once the user is on the page.
  pageAnchor?: TourAnchor;
  /** Step acts through the quick-subscribe modal instead of a page handoff. */
  quickAction?: 'subscribe';
  tipTitle: string;
  tipBody: string;
  task: string;
  cta: string;
}

// ORDER IS THE FUNNEL, and it is constrained by what a user can actually DO.
// Welcome carries the value story (what the platform is, the capability grid).
// Plans must come next: adding a trading account requires seats, and Copier and
// Logs are licence-gated too — so every hands-on step after this one is
// impossible without a plan. Moving Plans later reads better on a slide and
// breaks in the product: the user lands on Account Configuration with a
// disabled "Add Account" button and a tooltip telling them to upgrade.
export const TOUR_STEPS: TourStepDef[] = [
  {
    key: 'welcome',
    railTitle: 'Welcome',
    railHint: 'What this is',
    tipTitle: '',
    tipBody: '',
    task: '',
    cta: ''
  },
  {
    key: 'plans',
    railTitle: 'Plans & pricing',
    railHint: 'Pick a plan',
    // No menu handoff: the ticket is the purchase. A guided walkthrough runs
    // on its rows, Continue opens the checkout drawer on that exact cart, and
    // the drawer's own walkthrough carries the card and the purchase. The
    // route is for a user who already holds a plan — changes to it belong in
    // Billing, and Continue hands off there like any other step.
    quickAction: 'subscribe',
    route: ROUTES_TOUR.BILLING,
    tipTitle: 'Billing',
    tipBody: 'Licences are per connected account. A copier links two accounts, so you need at least two.',
    // Read on the Billing handoff only, i.e. by a user who already holds a plan.
    task: 'You already have a plan. Change its accounts or tier from here — nothing changes until you confirm.',
    cta: 'Done here'
  },
  {
    key: 'accounts',
    railTitle: 'Your accounts',
    railHint: 'Account Configuration',
    navMenuId: 'account-configuration',
    route: ROUTES_TOUR.ACCOUNTS,
    pageAnchor: TOUR_ANCHOR.ACCOUNTS_ADD,
    tipTitle: 'Account Configuration',
    tipBody: 'Connect the accounts you want to link. You need a master to copy from and a slave to copy to.',
    task: 'Click Add Account and connect the account you want to copy from.',
    cta: 'Done here'
  },
  {
    key: 'copier',
    railTitle: 'The copier',
    railHint: 'Copier Engine',
    navMenuId: 'copier-engine',
    route: ROUTES_TOUR.COPIER,
    pageAnchor: TOUR_ANCHOR.COPIER_ADD,
    tipTitle: 'Copier Engine',
    tipBody: 'Build the link between two accounts and choose how lot size is translated.',
    task: 'Click Add Master Copier, and we will walk you through every setting in the panel that opens.',
    cta: 'Done here'
  },
  {
    key: 'logs',
    railTitle: 'Copier logs',
    railHint: 'Copier Logs',
    navMenuId: 'copier-logs',
    route: ROUTES_TOUR.COPIER_LOGS,
    // No pageAnchor and no walkthrough: the page is a plain read, so we hand the
    // user over and get out of the way.
    tipTitle: 'Copier Logs',
    tipBody: 'Every copied trade, with the master and the slave side by side.',
    task: 'Have a look around. This is where you check a trade actually landed, and why the slave was sized the way it was.',
    cta: 'Done here'
  },
  {
    key: 'community',
    railTitle: 'Community',
    railHint: 'Discord',
    tipTitle: '',
    tipBody: '',
    task: '',
    cta: ''
  }
];

export const LAST_TOUR_STEP = TOUR_STEPS.length; // one past the rail = the done screen
export const LAST_RAIL_STEP = TOUR_STEPS.length - 1; // last numbered step in the rail

// Steps that hand off to a real page. Derived, so adding a step cannot leave a
// stale count behind in the rail, the progress ring or the closing summary.
export const HANDOFF_STEP_COUNT = TOUR_STEPS.filter((s) => s.navMenuId).length;

// One definition of "seen". The rail ticked a step off as soon as you moved past
// it, while the ring and the closing summary counted only completed handoffs —
// so reading the tour through without clicking in showed five ticks beside 0%.
export const isStepSeen = (index: number, step: number, visited: Record<number, boolean>) => !!visited[index] || index < step;

export const seenHandoffCount = (step: number, visited: Record<number, boolean>) =>
  TOUR_STEPS.reduce((n, s, i) => (s.navMenuId && isStepSeen(i, step, visited) ? n + 1 : n), 0);
